/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosResponse, AxiosHeaders } from 'axios';
import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { Region } from 'src/core/types/regionTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    InputDocument,
    SupportedLanguagesSwagger,
    TextTranslationSwagger,
    TranslationDocumentSwagger,
    TranslationJobSwagger,
    TranslationOperationSwagger,
    TranslationStateSwagger
} from 'src/languageStudio/core/services/translation/translatorSwagger';

/**
 * Represents a service for translation operations.
 */
export const getTranslationService = createService(({ context, getEnvironment }) => {
    const { apiUrl: translationBaseUrl } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, true)
        .useCustomHeader('x-ms-sender', 'LanguageStudio')
        .build();

    const getDocumentTranslationUrl = (resourceName?: string) =>
        urlBuilder()
            .baseUrl(`https://${resourceName}.cognitiveservices.azure.com`)
            .resource('translator', 'text')
            .resource('batch', 'v1.0')
            .resource('batches');

    /**
     * Gets the set of languages currently supported by other operations of the Translator.
     */
    const getSupportedLanguages = async (): Promise<SupportedLanguagesSwagger> => {
        const getUrl = urlBuilder()
            .baseUrl('https://api.cognitive.microsofttranslator.com') // Hardcoding translator global domain for private endpoint resources
            .resource('languages')
            .queryParam('api-version', '3.0')
            .build();

        const { data: supportedLanguages } = await httpService.get<SupportedLanguagesSwagger>(getUrl, apiOptions);

        return supportedLanguages;
    };

    /**
     * Translates text.
     */
    const translateText = async ({
        text,
        sourceLanguage,
        targetLanguage,
        deleteProfanity,
        transliteration,
        resourceLocation
    }: {
        text: string;
        targetLanguage: string;
        sourceLanguage?: string;
        resourceLocation: Region;
        deleteProfanity?: boolean;
        transliteration?: boolean;
    }): Promise<TextTranslationSwagger> => {
        let getUrl = urlBuilder()
            .baseUrl(translationBaseUrl)
            .resource('translate')
            .queryParam('api-version', '3.0')
            .queryParam('to', targetLanguage)
            .queryParam('includeSentenceLength', 'true');

        const textTranslationApiOptions = luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, true)
            .useCustomHeader('ocp-apim-subscription-region', resourceLocation)
            .build();

        if (sourceLanguage) {
            getUrl = getUrl.queryParam('from', sourceLanguage);
        }

        if (deleteProfanity) {
            getUrl = getUrl.queryParam('profanityAction', 'Deleted');
        }

        if (transliteration) {
            getUrl = getUrl.queryParam('toScript', 'Latn');
        }

        const { data: translations } = await httpService.post<TextTranslationSwagger[]>(
            getUrl.build(),
            [{ Text: text }],
            textTranslationApiOptions
        );

        return translations[0];
    };

    /**
     * Translates documents.
     */
    const translateDocuments = async ({
        documents,
        resourceName
    }: {
        documents: InputDocument[];
        resourceName: string;
    }): Promise<string> => {
        const getUrl = getDocumentTranslationUrl(resourceName).build();

        const { headers } = await httpService.post<TextTranslationSwagger[]>(getUrl, { inputs: documents }, apiOptions);
        const startTranslationOperationUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.LocationHeader];

        return startTranslationOperationUrl;
    };

    /**
     * Translates documents and fetch the job documents.
     */
    const translateDocumentsAndFetchDocs = async ({
        handler,
        documents,
        resourceName
    }: {
        documents: InputDocument[];
        handler: OperationHandler<TranslationStateSwagger<TranslationDocumentSwagger>>;
        resourceName: string;
    }): Promise<void> => {
        const getUrl = getDocumentTranslationUrl(resourceName).build();

        const { headers } = await httpService.post<TextTranslationSwagger[]>(getUrl, { inputs: documents }, apiOptions);

        const startTranslationOperationUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.LocationHeader];

        await fetchDocumentsByOperationUrl({ startTranslationOperationUrl, handler, resourceName });
    };

    const fetchDocumentsByOperationUrl = async ({
        startTranslationOperationUrl,
        handler,
        resourceName
    }: {
        startTranslationOperationUrl: string;
        handler: OperationHandler<TranslationStateSwagger<TranslationDocumentSwagger>>;
        resourceName: string;
    }): Promise<void> => {
        // Check for the translation operation itself before checking the documents.
        let trialNumber = 5;
        const { data: translationOperationStatus } = await httpService.get<TranslationOperationSwagger>(
            startTranslationOperationUrl,
            apiOptions
        );
        let startTranslationOperationSwagger = translationOperationStatus;

        // Need to wait until the operation starts executing.
        while (startTranslationOperationSwagger.status === 'NotStarted') {
            startTranslationOperationSwagger = (
                await httpService.get<TranslationOperationSwagger>(startTranslationOperationUrl, apiOptions)
            ).data;

            await new Promise(res => setTimeout(res, OperationDelayStrategies.exponentialDelayStrategy(null, trialNumber++)));
        }

        if (translationOperationStatus.status === 'Failed' || translationOperationStatus.status === 'ValidationFailed') {
            const error = new Error(translationOperationStatus.error?.message ?? 'Failed to start translation operation');
            if (translationOperationStatus.error) {
                error.name = `${translationOperationStatus.error.code}: ${translationOperationStatus.error.innerError.code}`;
            }

            throw error;
        }

        const operationId = startTranslationOperationUrl.split('batches/')[1];
        const documentStatusUrl = getDocumentTranslationUrl(resourceName)
            .resource(operationId)
            .resource('documents')
            .build();

        await createTranslatorOperationCallAndPoll(apiOptions, handler, () => documentStatusUrl);
    };

    const pollTranslationsStatus = async ({
        handler,
        resourceName
    }: {
        handler: OperationHandler<TranslationStateSwagger<TranslationJobSwagger>>;
        resourceName: string;
    }): Promise<void> => {
        const getUrl = getDocumentTranslationUrl(resourceName).build();

        await createTranslatorOperationCallAndPoll<TranslationJobSwagger>(apiOptions, handler, () => getUrl);
    };

    const getTranslationDocumentsStatus = async ({
        id,
        handler,
        resourceName
    }: {
        id: string;
        handler: OperationHandler<TranslationStateSwagger<TranslationDocumentSwagger>>;
        resourceName: string;
    }): Promise<void> => {
        const getUrl = getDocumentTranslationUrl(resourceName)
            .resource(id, 'documents')
            .build();

        await createTranslatorOperationCallAndPoll<TranslationDocumentSwagger>(apiOptions, handler, () => getUrl);
    };

    /**
     * Cancel translation job.
     */
    const cancelTranslation = async ({ jobId, resourceName }: { jobId: string; resourceName: string }): Promise<void> => {
        const deleteUrl = getDocumentTranslationUrl(resourceName)
            .resource(jobId)
            .build();

        await httpService.delete<TextTranslationSwagger[]>(deleteUrl, apiOptions);
    };

    return {
        translateText,
        cancelTranslation,
        translateDocuments,
        getSupportedLanguages,
        pollTranslationsStatus,
        fetchDocumentsByOperationUrl,
        getTranslationDocumentsStatus,
        translateDocumentsAndFetchDocs
    };
});

const createTranslatorOperationCallAndPoll = async <T extends TranslationOperationSwagger>(
    apiOptions: LuisRequestOptions,
    handler?: OperationHandler<TranslationStateSwagger<T>>,
    onGetPollUrl?: () => string
): Promise<AxiosResponse<TranslationStateSwagger<T>>> => {
    const pollUrl = onGetPollUrl?.();
    const pollHandler: OperationHandler<TranslationStateSwagger<T>> = handler ?? {
        retryStrategy: res =>
            res.data.value.reduce((acc, document) => acc || document.status === 'Running' || document.status === 'NotStarted', false),
        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy
    };

    return await createOperationCall(pollUrl, apiOptions, pollHandler, 0, async () => {
        let url = pollUrl;
        let response: AxiosResponse<TranslationStateSwagger<T>> = {
            status: 0,
            config: {
                headers: new AxiosHeaders(), // Mock headers (can be an empty object or include some mock headers)
                // Include any other necessary fields of InternalAxiosRequestConfig here, if needed
                method: 'get', // Example of an additional field that might be required
                url: '', // Example URL
                timeout: 0 // Timeout
                // Add any other properties as needed for InternalAxiosRequestConfig
            },
            headers: {},
            statusText: '',
            data: { value: [], '@nextLink': null }
        };

        while (url) {
            const extendedResponse = await httpService.get<TranslationStateSwagger<T>>(url, apiOptions);
            response = {
                ...response,
                data: { value: [...response.data.value, ...extendedResponse.data.value], '@nextLink': extendedResponse.data['@nextLink'] }
            };
            url = extendedResponse.data['@nextLink'];
        }

        return response;
    });
};
